import type { SelfReport } from '../../lib/api'
import {
  CAFFEINE_LAST_LABELS, INTENSITY_LABELS, NUMERIC_FIELDS, QUALITY_LABELS, TAG_LABELS, WHEN_AGO_LABELS, hasAnswer, numericText,
} from '../../lib/selfReport'
import { Icon, type IconName } from '../ui'

/** Port of the DS's six fixed-order journal icons. Answer presence only, never a grade. */
export function ContextStrip({ report }: { report: SelfReport | null | undefined }) {
  if (report == null) return <span className="day-context__skipped">Journal skipped</span>
  const sleep = [
    hasAnswer(report.sleep_hours) ? `Slept ${numericText(report.sleep_hours)} h` : null,
    hasAnswer(report.sleep_quality) ? QUALITY_LABELS[report.sleep_quality!] ?? report.sleep_quality : null,
    hasAnswer(report.woke_at_local) ? `woke ${report.woke_at_local}` : null,
  ].filter((value) => value !== null).join(' · ')
  const exercise = [
    hasAnswer(report.exercise_finished) ? `finished ${WHEN_AGO_LABELS[report.exercise_finished!] ?? report.exercise_finished}` : null,
    hasAnswer(report.exercise_intensity) ? INTENSITY_LABELS[report.exercise_intensity!] ?? report.exercise_intensity : null,
    hasAnswer(report.exercise_minutes) ? `${numericText(report.exercise_minutes)} ${NUMERIC_FIELDS.exercise_minutes.unit}` : null,
  ].filter((value) => value !== null).join(' · ')
  const facts: readonly { key: string; icon: IconName; text: string | null }[] = [
    { key: 'sleep', icon: 'moon', text: sleep === '' ? null : sleep },
    { key: 'caffeine', icon: 'coffee', text: hasAnswer(report.caffeine_last)
      ? `Coffee · ${CAFFEINE_LAST_LABELS[report.caffeine_last!] ?? report.caffeine_last}`
      : hasAnswer(report.caffeine_cups) ? `Coffee · ${numericText(report.caffeine_cups)} ${NUMERIC_FIELDS.caffeine_cups.unit}` : null },
    { key: 'exercise', icon: 'dumbbell', text: report.exercised === false ? 'No exercise'
      : report.exercised === true || exercise !== '' ? `Exercise${exercise === '' ? '' : ` · ${exercise}`}` : null },
    { key: 'water', icon: 'droplet', text: hasAnswer(report.water_glasses) ? `Water · ${numericText(report.water_glasses)} ${NUMERIC_FIELDS.water_glasses.unit}` : null },
    { key: 'alcohol', icon: 'wine', text: hasAnswer(report.alcohol_drinks) ? `Alcohol · ${numericText(report.alcohol_drinks)} ${NUMERIC_FIELDS.alcohol_drinks.unit}` : null },
    { key: 'tags', icon: 'tag', text: report.tags == null || report.tags.length === 0 ? null : report.tags.map((tag) => TAG_LABELS[tag] ?? tag).join(' · ') },
  ]
  return (
    <span className="day-context" role="group" aria-label="Journal context">
      <span className="day-context__icons">
        {facts.map((fact) => (
          <span className={fact.text === null ? 'day-context__icon day-context__icon--unanswered' : 'day-context__icon'} key={fact.key} title={fact.text ?? `${fact.key} · not answered`} aria-hidden={fact.text !== null ? true : undefined}>
            <Icon name={fact.icon} />
            {fact.text === null && <span className="visually-hidden">{fact.key} · not answered</span>}
          </span>
        ))}
      </span>
      {facts.filter((fact) => fact.text !== null).map((fact) => <span className="pill" key={fact.key}>{fact.text}</span>)}
    </span>
  )
}
